import * as React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactTitle,
  DataTable,
  HelpPopover,
  PageHeader,
  SplitList,
  STICKY_TABLE_TOP,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type ICompactChip,
  type IDataColumn,
  WorkControls,
  openRow,
  PaneControl,
  usePanePrefs,
  useOpenRecord
} from '@/design';
import { oversightApi } from '../api/oversight-api';
import type {
  ICatalogCycle,
  ICatalogSurvey,
  IOversightResponseRow,
  IPage,
  IResponseQuery,
  ResponseStatus
} from '../api/oversight-contracts';
import { LoadError, PastTheEnd, pageOf, useAdminData } from '../components/admin-page';
import { ListArea, usePaneRead } from '../components/list-area';
import type { ICardLinkState } from '../components/participant-link';
import { ResponseStatusChip, responseColumns } from '../components/responses-table';
import { RowNameClicks } from '../components/row-name-click';
import { TEAM_PATHS, responsePath } from '../paths';
import { refusalMessage } from '../refusal';
import { FunnelChoice, FunnelHeader, ParamFunnel } from './compliance/queue-headers';
import { useOwnColumns } from './lists/work-page/columns';
import { WorkPageHeaderActions, useReadAt } from './lists/work-page/header-actions';
import {
  WORK_ITEMS_MAX_PAGE,
  WorkPager,
  dropPaging,
  readItemPages,
  usePerPage,
  useWorkPaging,
  windowOf
} from './lists/work-page/pager';
import { RecordSearch } from './record-lists/record-work-page';
import { ParamChip } from './sec-adv/firm-filters';

/**
 * Responses across every participant — which is what makes this its own screen rather than
 * a wider `/my/cycles`: that endpoint scopes `responses[]` to the caller's own participant
 * links even for staff and admin, deliberately, so an oversight view has to come from
 * somewhere else: `GET /api/v1/admin/responses`, under `responses.browse`.
 *
 * **A light work page** (SR-504, `docs/superpowers/specs/2026-10-28-work-pages-design.md`): a
 * header with the total, the search, the funnels and the filter line, the table and the pager —
 * no cards, no signals, no counts read. A response opens beside the list (`AdminResponsesArea`),
 * whose pane reads the list's own query and window.
 *
 * **The list is the URL** — `q`, `survey`, `cycle`, `status` and the window (`page`, `size`) — so
 * leaving for a response and coming back restores it, and a filtered list can be pasted. A value
 * the URL names that no position stands for is still sent, and said as spelled: survey-api's 400
 * names it, in the page's words (`refusalMessage`). The last answer stays, dimmed, while the next
 * is out.
 *
 * **The search is the app's record search**, survey-api's (`q`, People's engine): it keeps the
 * responses of the participants it finds by name, firm, team, CRD, SEC or NPN number, email or
 * Salesforce id, read as typed over the whole list — never a filter over the rows fetched.
 *
 * **A funnel is single-valued** and offers the filter catalogue (`useResponseCatalogue`), uncounted:
 * the response list has no facets read. An old address repeating `status` is sent whole and said
 * as one chip; the cycles offered narrow with the survey chosen, since cycle codes repeat across
 * surveys.
 *
 * Two consequences of the route are visible here and are not omissions:
 *
 * - **No account column.** A response is addressed to a participant; the row carries no
 *   account, because more than one may act for the same participant.
 * - **The server sorts.** `updatedAt DESC` is fixed, so there is no sortable header to offer.
 *
 * A status is deliberately not preselected: omitting it means every status but `recycled`,
 * `archived` included. **The recycle bin is its own list** (`recycle-bin-page.tsx`, survey-api's
 * `?status=recycled`), so the Status funnel does not offer `recycled`; an address that names it is
 * sent as spelled, like any other value.
 */
export function AdminResponsesPage() {
  const location = useLocation();
  // Beside the list as *Opens in* says — its column, or a sheet over this page (SR-530).
  const openRecord = useOpenRecord();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const catalogue = useResponseCatalogue();

  const paging = useWorkPaging();
  const { page, perPage, pages } = paging;
  const query = responsesQueryOf(params, page, perPage);
  const asked = JSON.stringify([query, pages]);
  const filtered = FUNNEL_KEYS.some((name) => params.getAll(name).some((value) => value !== ''));
  const { data, error, reading, reload } = useAdminData<IAnswered>(
    async (signal) => {
      try {
        return {
          asked,
          searched: query.q !== undefined,
          filtered,
          page: await readItemPages(
            (at, pageSize) => oversightApi.listResponses({ ...query, page: at, pageSize }, signal),
            page,
            perPage,
            pages,
            WORK_ITEMS_MAX_PAGE
          )
        };
      } catch (listError) {
        throw new Error(refusalMessage(listError, 'Could not load this page'));
      }
    },
    [asked]
  );
  // The last answer while the next is out; nothing once this query's read has failed. An error
  // stands only while no newer read is out.
  const failed = error !== null && !reading;
  const shown = failed && data?.asked !== asked ? null : data;
  const readAt = useReadAt(data);

  /** A change of search or filter starts at the top — made on the address as it is when it
   *  lands, so the search's delayed write never undoes a filter set meanwhile. */
  const update = (mutate: (next: URLSearchParams) => void, replace = false) =>
    setParams(
      (current) => {
        const next = dropPaging(new URLSearchParams(current));
        mutate(next);
        return next;
      },
      { replace }
    );
  // Sending the query already on screen asks again.
  const search = (text: string) => {
    if (text === q) void reload();
    else update((next) => (text === '' ? next.delete('q') : next.set('q', text)), true);
  };
  // A version is one survey's: a new survey drops it.
  const choose = (name: string) => (value: Nullable<string>) =>
    update((next) => {
      if (value === null || value === '') next.delete(name);
      else next.set(name, value);
      if (name === 'survey') next.delete('version');
    });
  const clearAll = () => setParams(new URLSearchParams());

  // The funnels' values: the catalogue's, the cycles those of the survey chosen; a value the
  // address names that they lack is offered as spelled.
  const survey = params.get('survey') || null;
  const cycleCodes = React.useMemo(() => {
    const relevant = catalogue.cycles.filter((row) => survey === null || row.survey.code === survey);
    return [...new Set(relevant.map((row) => row.cycle.code))].sort();
  }, [catalogue.cycles, survey]);
  const funnels: IResponseFunnel[] = [
    {
      param: 'survey',
      column: 'survey',
      label: 'Survey',
      options: catalogue.surveys.map((row) => ({ value: row.code, label: row.name }))
    },
    {
      param: 'cycle',
      column: 'cycle',
      label: 'Cycle',
      options: cycleCodes.map((code) => ({ value: code, label: code }))
    },
    {
      // SR-265's AC1: by version too — each survey's, "FA v2", those of the survey chosen.
      param: 'version',
      column: 'version',
      label: 'Version',
      options: catalogue.surveys
        .filter((row) => survey === null || row.code === survey)
        .flatMap((row) =>
          row.versions.map((version) => ({
            value: version.surveyVersionId,
            label: `${row.code} v${version.versionNo}`
          }))
        )
    },
    {
      param: 'status',
      column: 'status',
      label: 'Status',
      options: STATUSES.map((status) => ({ value: status, label: status }))
    }
  ];
  const [funnelOpen, setFunnelOpen] = React.useState<Nullable<string>>(null);
  // A version is named from every survey's, so one the survey choice no longer offers still reads.
  const versionLabels = React.useMemo(
    () =>
      new Map(
        catalogue.surveys.flatMap((row) =>
          row.versions.map((version) => [version.surveyVersionId, `${row.code} v${version.versionNo}`] as const)
        )
      ),
    [catalogue.surveys]
  );
  const labelOf = (funnel: IResponseFunnel, value: string) =>
    funnel.options.find((option) => option.value === value)?.label ??
    (funnel.param === 'version' ? versionLabels.get(value) : undefined) ??
    value;
  const saidOf = (funnel: IResponseFunnel): Nullable<string> => {
    const values = params.getAll(funnel.param).filter((value) => value !== '');
    return values.length === 0 ? null : values.map((value) => labelOf(funnel, value)).join(', ');
  };
  const funnelBody = (funnel: IResponseFunnel) => {
    const value = params.get(funnel.param) || null;
    const unknown = value !== null && !funnel.options.some((option) => option.value === value);
    return (
      <FunnelChoice
        label={funnel.label}
        value={value}
        options={[
          { value: null, label: 'Any' },
          ...funnel.options,
          ...(unknown ? [{ value, label: value }] : [])
        ]}
        onChoose={(next) => {
          choose(funnel.param)(next);
          setFunnelOpen(null);
        }}
      />
    );
  };

  const linkState: ICardLinkState = { search: location.search, list: true };
  // A response beside the list (the row's first icon), or by *Opens in* (a press on the row).
  const prefs = usePanePrefs();
  const beside = (response: IOversightResponseRow) =>
    openRecord(responsePath(response.responseId), linkState);
  // The firm beside the participant, so a row the search found by its firm says why; the ways in
  // first on the row (the table's own first column), kept out of the arrangement.
  const [open, participant, ...rest] = responseColumns(linkState, true, false, beside);
  const listed: IDataColumn<IOversightResponseRow>[] = [participant, FIRM_COLUMN, ...rest];
  const own = listed.map((column): IDataColumn<IOversightResponseRow> => {
    const funnel = funnels.find((entry) => entry.column === column.id);
    if (!funnel) return column;
    return {
      ...column,
      label: funnel.label,
      header: (
        <FunnelHeader
          label={funnel.label}
          funnel={
            <ParamFunnel
              code={funnel.column}
              label={funnel.label}
              said={saidOf(funnel)}
              open={funnelOpen === funnel.column}
              onOpenChange={(open) => setFunnelOpen(open ? funnel.column : null)}
            >
              {funnelBody(funnel)}
            </ParamFunnel>
          }
        />
      )
    };
  });
  const chosen = useOwnColumns<IOversightResponseRow>('responses');
  const columns = chosen.resolve(own);
  const columnsButton = chosen.button(own);

  // Every filter applied, as a chip with its value — under the toolbar and on the compact bar's
  // second row; a press opens its column's funnel, ✕ drops it. The search makes no chip.
  const appliedChips: ICompactChip[] = funnels.flatMap((funnel) => {
    const said = saidOf(funnel);
    if (said === null) return [];
    return [
      {
        key: funnel.param,
        chip: (
          <ParamChip
            words={`${funnel.label}: ${said}`}
            body={funnelBody(funnel)}
            onPress={() => {
              const drawn = document.querySelector(`[data-funnel="${CSS.escape(funnel.column)}"]`);
              if (drawn === null) return false;
              setFunnelOpen(funnel.column);
              return true;
            }}
            onRemove={() => choose(funnel.param)(null)}
          />
        )
      }
    ];
  });
  const toolbar = useWorkToolbar();

  const tableState: DataTableState | undefined =
    shown === null
      ? failed
        ? { kind: 'error', content: <LoadError message={error} /> }
        : { kind: 'loading' }
      : shown.page.items.length === 0
        ? {
            kind: 'empty',
            content:
              shown.page.total > 0 ? (
                <PastTheEnd
                  page={shown.page.page}
                  pageSize={shown.page.pageSize}
                  total={shown.page.total}
                  counted={['response', 'responses']}
                  onPage={paging.setPage}
                />
              ) : (
                emptyStatement(shown)
              )
          }
        : undefined;

  // Where the page's own controls end: scrolled under the app bar, the compact bar stands for them.
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);
  const refresh = { reading, readAt, onClick: () => void reload() };
  const help = <AboutResponses />;
  const total = shown?.page.total ?? null;
  const searchBox = (compact: boolean) => (
    <RecordSearch
      q={q}
      label="Filter responses"
      placeholder="Filter responses…"
      onSearch={search}
      compact={compact}
    />
  );

  return (
    <>
      <PageHeader
        title="Responses"
        row
        actions={
          <WorkPageHeaderActions
            summary={
              total === null
                ? null
                : `${total.toLocaleString('en-US')} ${total === 1 ? 'response' : 'responses'}`
            }
            summaryData={{ 'data-responses-summary': '' }}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled}
        label="Responses, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={appliedChips.length > 0 && <CompactChipLine chips={appliedChips} onClearAll={clearAll} />}
      >
        <CompactTitle>Responses</CompactTitle>
        <CompactDivider />
        {searchBox(true)}
        <span className="flex-1" />
        <CompactDivider />
        {columnsButton}
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        <WorkControls end={setControlsEnd}>
          <WorkToolbar
            toolbar={toolbar}
            status={{
              reading,
              count: shown ? `${shown.page.total.toLocaleString('en-US')} found` : undefined
            }}
            search={searchBox(false)}
            quick={null}
            quickCount={0}
            end={
              <>
                {columnsButton}
                <PaneControl prefs={prefs} kind="record" />
              </>
            }
          />
          {appliedChips.length > 0 && (
            <CompactChipLine label="Filters" chips={appliedChips} onClearAll={clearAll} />
          )}
        </WorkControls>

        {failed && shown !== null && (
          <LoadError message={`Could not refresh — showing the list as last read. ${error}`} />
        )}

        <RowNameClicks>
          <DataTable
            label="Responses"
            columns={[open, ...columns]}
            rows={tableState ? null : (shown?.page.items ?? null)}
            rowKey={(response) => response.responseId}
            onOpen={(response) =>
              openRow(prefs, responsePath(response.responseId), () => beside(response))
            }
            busy={reading && shown !== null}
            state={tableState}
            stickyTop={STICKY_TABLE_TOP}
          />
        </RowNameClicks>

        {shown && <WorkPager {...paging.pager} total={shown.page.total} busy={reading} sticky />}
      </div>
    </>
  );
}

/** The "?": what the list holds, and how its search and funnels read. */
function AboutResponses() {
  return (
    <HelpPopover label="About Responses" align="end">
      <p>
        Every participant&apos;s responses, the most recently changed first — every status but the
        recycled ones, which are the recycle bin&apos;s. Open a response to read it beside the list.
      </p>
      <p>
        The search reads as you type. It finds the responses of the advisors, firms and teams the
        app&apos;s search finds — by name, firm or team name, CRD, SEC or NPN number, email or
        Salesforce id — every word matched.
      </p>
      <p>
        A row opens beside the list by its first icon and in a new tab by ↗; a click on the row or
        its survey goes where «Opens in» says.
      </p>
      <p>
        A column&apos;s funnel narrows the list to one of its values; the cycles offered are those of
        the survey chosen. Clear all drops the search and every filter.
      </p>
    </HelpPopover>
  );
}

/** A single-valued funnel of a known vocabulary: its URL key, the column it heads, its values. */
interface IResponseFunnel {
  param: string;
  column: string;
  label: string;
  options: readonly { value: string; label: string }[];
}

/** The funnels' URL keys — what makes an empty list "none matches" rather than "none held". */
const FUNNEL_KEYS = ['survey', 'cycle', 'version', 'status'];

/** A page of responses, with the query it answers and what narrowed it. */
interface IAnswered {
  asked: string;
  searched: boolean;
  filtered: boolean;
  page: IPage<IOversightResponseRow>;
}

/** Says what narrowed the list, so "none matches" is never read as "none is held". */
function emptyStatement(answer: IAnswered): string {
  if (answer.searched && answer.filtered) return 'No response matches this search with these filters.';
  if (answer.searched) return 'No response matches this search.';
  if (answer.filtered) return 'No response matches these filters.';
  return 'No response is held yet.';
}

/** The URL's filters as the list's query — one function for the list and its pane. */
export function responsesQueryOf(
  params: URLSearchParams,
  page = pageOf(params.get('page')),
  pageSize?: number
): IResponseQuery {
  return {
    q: params.get('q')?.trim() || undefined,
    surveyCode: params.get('survey') ?? undefined,
    cycleCode: params.get('cycle') ?? undefined,
    surveyVersionId: params.get('version') ?? undefined,
    // Sent as the URL spells them: an unknown status is survey-api's 400, by name.
    status: params.getAll('status').filter((status) => status !== '') as ResponseStatus[],
    page,
    pageSize
  };
}

/** Responses and a response, as one area: the list, or a response with the list beside it. */
export function AdminResponsesArea() {
  return (
    <ListArea
      param="responseId"
      list={TEAM_PATHS.responses}
      paneTitle="Responses"
      pane={(search, selected) => <ResponsesPane search={search} selected={selected} />}
    />
  );
}

/** The list as the pane beside a response draws it: who, which survey and cycle, the status. */
function ResponsesPane({ search, selected }: { search: string; selected: string }) {
  // The list's own Per page and window, so the URL's page is the same rows beside a response.
  const [perPage] = usePerPage();
  const { shown, pager, status } = usePaneRead(search, 'No response on this list.', (params, page, signal) => {
    const window = windowOf(params, perPage);
    return readItemPages(
      (at, pageSize) => oversightApi.listResponses(responsesQueryOf(params, at, pageSize), signal),
      page,
      perPage,
      page === window.page ? window.pages : 1,
      WORK_ITEMS_MAX_PAGE
    );
  });
  return (
    <SplitList
      label="Responses"
      selected={selected}
      items={
        shown?.items.map((response) => ({
          key: response.responseId,
          to: responsePath(response.responseId),
          state: { search, list: true } satisfies ICardLinkState,
          primary: response.participant.displayName,
          secondary: `${response.survey.code} ${response.cycle.code} · v${response.version.versionNo}`,
          trailing: <ResponseStatusChip status={response.status} />
        })) ?? null
      }
      status={status}
      footer={pager}
    />
  );
}

/** The catalogue the pickers are filled from — this list's and the recycle bin's. Losing it leaves
 *  the list usable with no survey or cycle to pick, rather than replacing the screen with an error
 *  it cannot act on. */
export function useResponseCatalogue(): { surveys: ICatalogSurvey[]; cycles: ICatalogCycle[] } {
  const [catalogue, setCatalogue] = React.useState<{
    surveys: ICatalogSurvey[];
    cycles: ICatalogCycle[];
  }>({ surveys: [], cycles: [] });
  React.useEffect(() => {
    const controller = new AbortController();
    void Promise.all([
      oversightApi.surveys(controller.signal),
      oversightApi.cycles(undefined, controller.signal)
    ])
      .then(([surveys, cycles]) => {
        if (!controller.signal.aborted) setCatalogue({ surveys, cycles });
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);
  return catalogue;
}

/** What the Status chip offers — every status but `recycled`, which is the recycle bin's list. */
const STATUSES: ResponseStatus[] = ['draft', 'submitted', 'locked', 'archived'];

/** The firm on the participant's profile; "—" where it names none. */
const FIRM_COLUMN: IDataColumn<IOversightResponseRow> = {
  id: 'firm',
  header: 'Firm',
  optional: true,
  cell: (response) => response.firmName ?? <span className="text-muted-foreground">—</span>
};
